"use client";

import * as React from "react";
import Link from "next/link";
import { useTableData } from "@/hooks/use-liquidity";
import type { PoolTableRow } from "@/lib/dexscreener";
import { resolveDexLogoSources } from "@/lib/dex-logos";
import { formatUsdCompact, formatUsdPrice } from "@/utils/format-price";
import { HEX_VARIANT_PILL } from "@/constants/chart-palette";
import { cn } from "@/utils";

function TokenIcon({
  src,
  symbol,
  className,
  rounded = "full",
}: {
  src: string;
  symbol: string;
  className?: string;
  rounded?: "full" | "md";
}) {
  const [failed, setFailed] = React.useState(false);

  if (failed || !src) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center border border-white/10 bg-surface-400 text-2xs font-bold uppercase text-white/70",
          rounded === "full" ? "rounded-full" : "rounded-md",
          className,
        )}
      >
        {symbol.slice(0, 2)}
      </span>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      className={cn(
        "bg-surface-400 object-cover",
        rounded === "full" ? "rounded-full" : "rounded-md",
        className,
      )}
      onError={() => setFailed(true)}
    />
  );
}

function DexLogo({
  dexId,
  poolUrl,
  dexName,
  className,
}: {
  dexId: string;
  poolUrl: string;
  dexName: string;
  className?: string;
}) {
  const sources = React.useMemo(
    () => resolveDexLogoSources(dexId, poolUrl),
    [dexId, poolUrl],
  );
  const [index, setIndex] = React.useState(0);
  const src = sources[index];

  if (!src || index >= sources.length) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-md border border-white/10 bg-surface-400 text-2xs font-bold uppercase text-white/70",
          className,
        )}
      >
        {dexName.slice(0, 2)}
      </span>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      className={cn("rounded-md bg-surface-400 object-contain p-0.5", className)}
      onError={() => setIndex((i) => i + 1)}
    />
  );
}

function PairIcons({ row }: { row: PoolTableRow }) {
  return (
    <div className="flex items-center -space-x-2">
      <TokenIcon
        src={row.sideA.logoUrl}
        symbol={row.sideA.symbol}
        className="relative z-10 h-7 w-7 ring-2 ring-surface-200"
      />
      <TokenIcon
        src={row.sideB.logoUrl}
        symbol={row.sideB.symbol}
        className="relative h-7 w-7 ring-2 ring-surface-200"
      />
    </div>
  );
}

function isHexSymbol(symbol: string) {
  const s = symbol.toUpperCase().replace(/[^A-Z]/g, "");
  return ["HEX", "EHEX", "PHEX"].includes(s);
}

function hexAndPairSides(row: PoolTableRow) {
  if (isHexSymbol(row.sideA.symbol)) {
    return { hex: row.sideA, pair: row.sideB };
  }
  return { hex: row.sideB, pair: row.sideA };
}

function formatReserve(amount: number) {
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  if (amount >= 1e12) return `${(amount / 1e12).toFixed(2)}T`;
  if (amount >= 1e9) return `${(amount / 1e9).toFixed(2)}B`;
  if (amount >= 1e6) return `${(amount / 1e6).toFixed(2)}M`;
  if (amount >= 1e3) return `${(amount / 1e3).toFixed(1)}K`;
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function LiquidityBreakdown({
  row,
  shareOfTotal,
}: {
  row: PoolTableRow;
  shareOfTotal: number;
}) {
  const { hex, pair } = hexAndPairSides(row);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-lg font-semibold text-white">
          {formatUsdCompact(row.liquidityUsd)}
        </p>
        <p className="text-right font-mono text-sm text-muted">
          {shareOfTotal.toFixed(1)}%
        </p>
      </div>

      <div
        className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]"
        title={`${shareOfTotal.toFixed(1)}% of filtered pool liquidity`}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-hex-400 to-hex-500 transition-[width] duration-300"
          style={{ width: `${Math.min(100, Math.max(0, shareOfTotal))}%` }}
        />
      </div>

      {[hex, pair].map((side) => (
        <div
          key={side.symbol}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
        >
          <TokenIcon src={side.logoUrl} symbol={side.symbol} className="h-5 w-5" />
          <span className="font-medium text-white">{side.symbol}</span>
          <span className="font-mono text-xs text-muted">
            {formatReserve(side.amount)}
          </span>
          <span className="text-white/20">·</span>
          <span className="font-mono text-xs text-muted">
            {formatUsdPrice(side.priceUsd)}
          </span>
          <span className="text-white/20">=</span>
          <span
            className={cn(
              "font-mono text-xs font-medium",
              isHexSymbol(side.symbol) ? "text-hex-300" : "text-white/80",
            )}
          >
            {formatUsdCompact(side.usd)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Table() {
  const { data, isLoading } = useTableData();
  const rows = (Array.isArray(data) ? data : []) as PoolTableRow[];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-hex-400" />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="py-20 text-center text-sm text-muted">
        No liquidity pools match the current filter.
      </div>
    );
  }

  const totalListedLiquidity = rows.reduce((sum, row) => sum + row.liquidityUsd, 0);

  return (
    <div className="min-w-[720px]">
      <div className="sticky top-0 z-10 grid grid-cols-[minmax(14rem,1.2fr)_minmax(18rem,1.8fr)] gap-4 border-b border-white/[0.06] bg-surface-200/95 px-5 py-3 backdrop-blur-md">
        <div className="label-caps px-1">Pool</div>
        <div className="label-caps px-1">Liquidity</div>
      </div>

      <div className="divide-y divide-white/[0.04]">
        {rows.map((row, idx) => (
          <div
            key={`${row.pool}-${idx}`}
            className="grid grid-cols-[minmax(14rem,1.2fr)_minmax(18rem,1.8fr)] gap-4 px-5 py-4 transition-colors hover:bg-white/[0.02]"
          >
            <div className="flex gap-3">
              <div className="flex flex-col items-center gap-1 pt-0.5">
                <TokenIcon
                  src={row.chainLogoUrl}
                  symbol={row.chain.slice(0, 2)}
                  className="h-5 w-5"
                />
                <DexLogo
                  dexId={row.dexId}
                  poolUrl={row.pool}
                  dexName={row.dex}
                  className="h-5 w-5"
                />
              </div>

              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <PairIcons row={row} />
                  <span className={HEX_VARIANT_PILL[row.hexVariant].className}>
                    {row.variantLabel}
                  </span>
                </div>
                <Link
                  href={row.pool}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-medium text-white transition-colors hover:text-hex-400"
                >
                  {row.pairs[0]}/{row.pairs[1]}
                </Link>
                <p className="text-xs text-muted">
                  {row.chain} · {row.dex}
                </p>
              </div>
            </div>

            <LiquidityBreakdown
              row={row}
              shareOfTotal={
                totalListedLiquidity > 0
                  ? (row.liquidityUsd / totalListedLiquidity) * 100
                  : 0
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
}
